import { Trans } from '@lingui/react';
import clsx from 'clsx';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import type { TryShadeCardProps } from '../../../components/cards/tryShade/TryShadeCard';
import { TryShadeCard } from '../../../components/cards/tryShade/TryShadeCard';
import {
  Shimmer,
  toBase64,
} from '../../../components/utility/imageShimmer/ImageShimmer';
import { ScrollSnapSlider } from '../../../components/utility/scrollSnapSlider/ScrollSnapSlider';
import type { ImageType } from '../../../types.d/storeTypes';
import styles from './TryShadeSection.module.scss';

export type TryShadeSectionProps = {
  backgroundColor?: string;
  image?: ImageType;
  tryShadeCards?: Array<TryShadeCardProps>;
  isLoading?: boolean;
};

/**
 * Try Shade Section component.
 * @param props - component props.
 */
export const TryShadeSection = ({
  backgroundColor,
  image,
  tryShadeCards,
  isLoading,
}: TryShadeSectionProps) => {
  if (isLoading) {
    return (
      <section
        className={styles.tryShadeSection__wrapper}
        style={{ '--background-color': backgroundColor } as CSSProperties}
      >
        <div className={clsx(styles.tryShadeSection, 'is-loading')}>
          <div
            className={clsx(styles.tryShadeSection__title, 'is-loading')}
          ></div>

          <div className={styles.tryShadeSection__listing}>
            {[...Array(6).keys()].map((index) => (
              <div key={index} className={styles.tryShadeSection__card}>
                <TryShadeCard {...({} as TryShadeCardProps)} isLoading={true} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (image?.url && tryShadeCards?.length) {
    const cards = tryShadeCards.map((tryShadeCard, index) => {
      return (
        <div key={index} className={styles.tryShadeSection__card}>
          <TryShadeCard {...tryShadeCard} />
        </div>
      );
    });

    return (
      <section
        className={styles.tryShadeSection__wrapper}
        style={{ '--background-color': backgroundColor } as CSSProperties}
      >
        <div className={styles.tryShadeSection}>
          <h2
            className={clsx(
              styles.tryShadeSection__title,
              'text-heading-h5-mobile text-heading-h5-desktop container'
            )}
          >
            <Trans
              id="tryShadeSection.title"
              message="You can also try this shade in..."
            />
          </h2>

          <ScrollSnapSlider
            spacing="20px"
            height="100%"
            dots={true}
            className={clsx(styles.tryShadeSection__carousel, 'mobile')}
            hideDisabledNavButton
          >
            {cards}
          </ScrollSnapSlider>

          <ScrollSnapSlider
            spacing="20px"
            height="100%"
            className={styles.tryShadeSection__carousel}
            hideDisabledNavButton
          >
            <div
              className={clsx(
                styles.tryShadeSection__card,
                styles.tryShadeSection__image
              )}
            >
              <Image
                src={image?.url}
                alt={image?.title}
                title={image?.title}
                width="405"
                height={image.height}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  Shimmer(image?.width as number, image?.height as number)
                )}`}
              />
            </div>

            {cards}
          </ScrollSnapSlider>
        </div>
      </section>
    );
  }

  return <></>;
};
